/**
 * Pure save-status vocabulary shared by the Table, the Pitch and the scenario
 * Pitch (SE-8389, PV23 · D5): the toolbar's one status line and the one-line
 * summary of a change kept as a copy after a conflict (D17).
 * @module features/scouting/utils/pitchSaveStatus
 */
import type { PitchSaveConflictBannerProps } from "@/features/scouting/components/molecules/PitchSaveConflictBanner";
import type { PitchSaveStatusLineProps } from "@/features/scouting/components/molecules/PitchSaveStatusLine";
import {
  applyRelativePreferenceOperation,
  isPitchAssignmentOperation,
  isPitchFormationOperation,
  type WatchlistPitchOperation,
  type WatchlistPreferenceFailure,
  type WatchlistPreferenceStatus,
} from "@/features/scouting/hooks/watchlistPreferenceOrderState";
import {
  FAILURE_BANNER_THRESHOLD,
  SAVE_CONFLICT_MESSAGE,
  SAVE_FAILED_MESSAGE,
} from "@/features/scouting/hooks/watchlistPreferenceOrderReducer";
import { formatElapsedAgo } from "@/utils/timeFormatting";

import { formatPitchFormation } from "./watchlistPitchPresentation";

/** The slice of a save controller the status line reads. */
export interface PitchSaveStatusSource {
  readonly status: WatchlistPreferenceStatus;
  /** Epoch milliseconds of the last successful save. */
  readonly savedAt?: number;
  /** Reducer-set copy that wins over the default for its status. */
  readonly message?: string;
  /**
   * Who saved the newer version. Absent today; SE-8392 adds `lastEditedBy`
   * to the pitch reads, and the post-conflict refetch is where it comes from.
   */
  readonly editorName?: string;
}

/**
 * The toolbar status text: Saving… · Saved just now → Saved 2m ago ·
 * Couldn't save · Retry · Updated by another editor · Reload.
 * @param source - Status, last save time and any reducer message
 * @param now - Wall clock, re-read on the 60 s tick
 * @param idleText - What the line reads when nothing has happened yet
 * @returns The status line
 */
export const pitchSaveStatusText = (
  source: PitchSaveStatusSource,
  now: number,
  idleText: string
): string => {
  switch (source.status) {
    case "saving":
      return "Saving…";
    case "saved":
      return `Saved ${formatElapsedAgo(now - (source.savedAt ?? now))}`;
    case "error":
      return source.message ?? SAVE_FAILED_MESSAGE;
    case "conflict":
      // The reducer stamps the generic copy on every conflict (it never
      // sees the read); a read that names the editor wins over that default
      // (SE-8393 wires `lastEditedBy` from the scenario pitch).
      return source.message && source.message !== SAVE_CONFLICT_MESSAGE
        ? source.message
        : source.editorName
          ? `Updated by ${source.editorName} · Reload`
          : SAVE_CONFLICT_MESSAGE;
    default:
      return idleText;
  }
};

/**
 * One line naming the user's change for the conflict banner: `A. Ruiz → LB`,
 * `A. Ruiz ↑ rank 1`, `Formation → 3-5-2`.
 * @param operation - The change kept as a copy
 * @param playerNames - Display names by player id
 * @param orderBefore - The order the change would be re-applied over
 * @returns The summary
 */
export const summarizePitchOperation = (
  operation: WatchlistPitchOperation,
  playerNames: Readonly<Record<string, string>>,
  orderBefore: readonly string[]
): string => {
  const nameOf = (playerId: string) => playerNames[playerId] ?? playerId;
  if (isPitchFormationOperation(operation)) {
    return `Formation → ${formatPitchFormation(operation.formation)}`;
  }
  if (isPitchAssignmentOperation(operation)) {
    return `${nameOf(operation.playerId)} → ${operation.tacticalSlot ?? "Unassigned"}`;
  }
  const move = operation.moves[0];
  if (!move) return "Reorder";
  const after = applyRelativePreferenceOperation(orderBefore, operation);
  const rankAfter = after.indexOf(move.playerId) + 1;
  const rankBefore = orderBefore.indexOf(move.playerId) + 1;
  const arrow = rankAfter <= rankBefore ? "↑" : "↓";
  return `${nameOf(move.playerId)} ${arrow} rank ${rankAfter}`;
};

/**
 * The slice of the preference-order controller the save surfaces read —
 * structural, so the scenario Pitch can satisfy it without the watchlist hook.
 */
export interface PitchSaveController extends PitchSaveStatusSource {
  readonly orderedPlayerIds: readonly string[];
  readonly failure?: WatchlistPreferenceFailure;
  readonly failedChange?: WatchlistPitchOperation;
  readonly heldChange?: WatchlistPitchOperation;
  readonly reapplyRefusal?: string;
  readonly retry: () => unknown;
  readonly discard: () => void;
  readonly reload: () => void;
  readonly reapply: () => unknown;
}

/**
 * What the Pitch's toolbar line reads before anything has been saved
 * (SE-8486, W14): nothing for an editor — the frame shows time only, the
 * `revision n` suffix SE-8389 carried is gone — and `Read-only` for a viewer.
 * @param canEdit - Whether the viewer may reorder
 * @returns The idle text
 */
export const pitchIdleText = (canEdit: boolean): string =>
  canEdit ? "" : "Read-only";

/**
 * Props for the toolbar status line from the controller.
 * @param controller - The save controller, absent before the read lands
 * @param idleText - What the line reads when nothing has happened yet
 * @returns Status-line props
 */
export const pitchSaveStatusProps = (
  controller: PitchSaveController | undefined,
  idleText: string
): PitchSaveStatusLineProps =>
  controller
    ? {
        status: controller.status,
        savedAt: controller.savedAt,
        message: controller.message,
        editorName: controller.editorName,
        idleText,
        onRetry: controller.retry,
        onReload: controller.reload,
      }
    : { status: "idle", idleText };

/**
 * Props for the save banner from the controller, or nothing to show: a
 * refused re-apply first (the copy is gone), then a held copy (D17), then a
 * save that failed three times.
 * @param controller - The save controller, absent before the read lands
 * @param listName - The list the banner names
 * @param playerNames - Display names by player id for the change summary
 * @returns Banner props, or undefined when no banner applies
 */
export const pitchSaveBannerProps = (
  controller: PitchSaveController | undefined,
  listName: string,
  playerNames: Readonly<Record<string, string>>
): PitchSaveConflictBannerProps | undefined => {
  if (!controller) return undefined;
  if (controller.reapplyRefusal) {
    return {
      variant: "refused",
      message: controller.reapplyRefusal,
      onDismiss: controller.reload,
    };
  }
  const isBusy = controller.status === "saving";
  if (controller.heldChange) {
    return {
      variant: "conflict",
      editorName: controller.editorName,
      listName,
      changeSummary: summarizePitchOperation(
        controller.heldChange,
        playerNames,
        controller.orderedPlayerIds
      ),
      onReload: controller.reload,
      onReapply: controller.reapply,
      isBusy,
    };
  }
  if (
    controller.failedChange &&
    (controller.failure?.attempts ?? 0) >= FAILURE_BANNER_THRESHOLD
  ) {
    return {
      variant: "failed",
      changeSummary: summarizePitchOperation(
        controller.failedChange,
        playerNames,
        controller.orderedPlayerIds
      ),
      onRetry: controller.retry,
      onDiscard: controller.discard,
      isBusy,
    };
  }
  return undefined;
};
